import * as React from 'react'

import { cn } from '@/utils'

interface SkeletonProps
  extends React.ComponentPropsWithoutRef<React.ElementType> {
  asChild: boolean
}

const Skeleton = React.forwardRef<HTMLSpanElement, SkeletonProps>(
  ({ className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex rounded-md bg-linear-to-r from-border/60 via-border/30 to-border/60 bg-size-[200%_100%] animate-shimmer',
          className,
        )}
        {...props}
      >
        {props.children}
        <br />
      </span>
    )
  },
)
Skeleton.displayName = 'Skeleton'

const PageSkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-2">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-5 w-72" />
    </div>
    <Skeleton className="h-64 w-full" />
  </div>
)

const SectionSkeleton = () => (
  <div className="space-y-4">
    <Skeleton className="h-32 w-full" />
    <Skeleton className="h-48 w-full" />
  </div>
)

const CardSkeleton = () => (
  <div className="space-y-3 rounded-lg border border-border bg-card p-6">
    <Skeleton className="h-5 w-32" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-3/4" />
  </div>
)

const SubnavContentSkeleton = () => (
  <div className="container mx-auto max-w-7xl px-4 py-8 space-y-8">
    <div className="space-y-2">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-5 w-96" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <CardSkeleton />
      <CardSkeleton />
      <CardSkeleton />
    </div>
    <Skeleton className="h-96 w-full" />
  </div>
)

const SectionContentSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Skeleton className="h-80 w-full" />
      <Skeleton className="h-80 w-full" />
    </div>
    {/* <TableSkeleton rows={8} /> */}
  </div>
)

const StatementsSkeleton = () => (
  <div className="min-h-0">
    <div className="rounded-lg border overflow-hidden">
      <div className="p-6 space-y-2">
        <Skeleton className="h-6 w-48" />
      </div>
      <div className="p-6 pt-0 space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </div>
  </div>
)

const InsightsIncomeSkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-1">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-5 w-96" />
    </div>
    <div className="rounded-lg border overflow-hidden">
      <div className="p-6 space-y-2">
        <Skeleton className="h-6 w-48" />
      </div>
      <div className="p-6 pt-0">
        <Skeleton className="h-[320px] w-full" />
      </div>
    </div>
    <div className="space-y-3">
      <div className="space-y-1">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-5 w-80" />
      </div>
      <div className="rounded-lg border overflow-hidden max-h-[500px]">
        <div className="p-4">
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    </div>
  </div>
)

const InsightsSpendingSkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-1">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-5 w-64" />
    </div>
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex gap-2">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-24" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-8 w-32" />
        </div>
      </div>
      <div className="rounded-lg border overflow-hidden">
        <div className="p-6 space-y-2">
          <Skeleton className="h-6 w-56" />
        </div>
        <div className="p-6 pt-0">
          <Skeleton className="h-[400px] w-full" />
        </div>
      </div>
    </div>
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex gap-2">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-24" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-8 w-32" />
        </div>
      </div>
      <div className="rounded-lg border overflow-hidden">
        <div className="p-6 space-y-2">
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="p-6 pt-0">
          <Skeleton className="h-[400px] w-full" />
        </div>
      </div>
    </div>
  </div>
)

const InsightsDuplicatesSkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-1">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-5 w-72" />
    </div>
    <div className="space-y-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={`duplicate-skeleton-${i}`} className="space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-16" />
            <div className="h-px flex-1 bg-border"></div>
          </div>
          <div className="rounded-lg border overflow-hidden">
            <div className="p-4 space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
)

const InsightsFlowOfFundsSkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-1">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-5 w-96" />
    </div>
    <div className="rounded-lg border overflow-hidden">
      <div className="p-6 space-y-2">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-5 w-80" />
      </div>
      <div className="p-6 pt-0">
        <Skeleton className="h-[600px] w-full" />
      </div>
    </div>
    <div className="rounded-lg border overflow-hidden">
      <div className="p-6 space-y-4">
        <Skeleton className="h-6 w-32" />
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    </div>
  </div>
)

export {
  Skeleton,
  PageSkeleton,
  SectionSkeleton,
  CardSkeleton,
  SubnavContentSkeleton,
  SectionContentSkeleton,
  StatementsSkeleton,
  InsightsIncomeSkeleton,
  InsightsSpendingSkeleton,
  InsightsDuplicatesSkeleton,
  InsightsFlowOfFundsSkeleton,
}
